<script setup>

import {onMounted, ref, provide, computed} from "vue";
import { useNotificationStore } from '@/stores/NotificationStore.js';

const notificationStore = useNotificationStore()

const props = defineProps({
    leadId: {
        type: Number,
        required: true,
    },
    asWidget: {
        type: Boolean,
        required: false,
        default: false
    }
})

const emit = defineEmits(['update:lead', 'archived', 'deleted'])

const currentTab = ref('details')
const tabs = ref([
    {
        label: _mft('crm:leadPage.tabTitle.showAll'),
        name: 'all'
    },
    {
        label: _mft('crm:leadPage.tabTitle.details'),
        name: 'details'
    },
    {
        label: _mft('crm:leadPage.tabTitle.contracts'),
        name: 'signatures'
    },
    {
        label: _mft('crm:leadPage.tabTitle.deliveries'),
        name: 'deliveries'
    },
    {
        label: _mft('crm:leadPage.tabTitle.notes'),
        name: 'notes'
    },
    {
        label: _mft('crm:leadPage.tabTitle.actions'),
        name: 'actions'
    },
    {
        label: _mft('crm:leadPage.tabTitle.licenses'),
        name: 'licenses'
    },
])

const lead = ref(null)

const restoring = ref(false)

const restoreLead = async () => {
    if (restoring.value) {
        return
    }

    restoring.value = true

    try {
        const res = await axios.post(route('api.crm.leads.unarchive'), {lead_ids: [lead.value.id]})

        notificationStore.addNotification({
            type: 'success',
            title: _mft('crm:archive.result.restored', {count: res.data.succeeded}),
            dismissAfter: 5000,
        })
        await loadLead()
    } catch (error) {
        notificationStore.addNotification({
            type: 'error',
            title: _mft('crm:archive.error'),
            dismissAfter: 5000,
        })
    } finally {
        restoring.value = false
    }
}

const setCurrentTab = (tab) => {
    currentTab.value = tab
}

const loadLead = async () => {
    const res = await axios.get(route('api.crm.lead.show', props.leadId))

    lead.value = res.data.lead
}

const updateLead = (_lead) => {
    lead.value = _lead
    emit('update:lead', _lead)
}

const showAll = computed(() => {
    return currentTab.value === 'all';
})

onMounted(() => {
    loadLead()

    if (props.legacyDeals === false) {
        tabs.value.push({
            label: _mft('crm:leadPage.tabTitle.deals'),
            name: 'deals'
        })
    }
})

provide('lead', lead)

</script>
<template>
    <div>
        <div v-if="lead === null" class="py-8">
            <mf-spinner-medium class="w-24 h-12" />
        </div>
        <div v-else>

            <frosted-bar
                v-if="! asWidget"
                :breadcrumbs="[
                    {url: route('crm.leads'), label: _mft('crm:allContacts')},
                    {url: route('crm.leads.view', lead.id), label: lead.name},
                ]"
            >
                <div class="flex items-center justify-between space-x-4">
                    <div class="flex items-center">
                        <div class="w-8 h-8 bg-wlPrimary text-wlPrimaryContrast flex justify-center items-center rounded-full text-xs font-bold uppercase">
                            {{ lead.name[0] }}
                            {{ lead.name.split(' ').length > 1 ? lead.name.split(' ')[1][0] : '' }}
                        </div>

                        <div class="ml-2 flex items-center">
                            <div>
                                <h1 class="text-lg leading-4 font-semibold">
                                    {{ lead.name }}
                                </h1>
                                <p class="text-xxs !mb-0">
                                    {{ lead.company}}
                                </p>
                            </div>
                        </div>
                    </div>

                    <lead-header-actions
                        :lead="lead"
                        @reload:lead="loadLead"
                        @archived="loadLead"
                    />
                </div>
            </frosted-bar>

            <teleport
                v-if="asWidget"
                to="#lead-quickview-top-bar"
                defer
            >
                <div class="flex items-center space-x-2">
                    <lead-actions-button
                        :lead="lead"
                        :allow-archive="true"
                        :redirect-after-delete="false"
                        :show-label="false"
                        @reload:lead="loadLead"
                        @archived="emit('archived')"
                        @deleted="emit('deleted')"
                    />

                    <lead-closer-actions :lead="lead" />
                </div>
            </teleport>

            <div
                v-if="lead.is_archived"
                class="flex flex-col gap-3 p-4 mb-4 text-sm border border-amber-200 bg-amber-50 rounded-card text-amber-900 md:flex-row md:items-center md:justify-between"
            >
                <div class="space-y-1">
                    <span class="block font-semibold">
                        <i class="mr-1 fa-regular fa-box-archive"></i>
                        {{ _mft('crm:archive.banner.title') }}
                    </span>
                    <span class="block">{{ _mft('crm:archive.banner.body') }}</span>
                    <span
                        v-if="lead.archived_at_tz"
                        class="block text-xs"
                    >
                        {{ _mft('crm:archive.banner.archivedAt', {date: lead.archived_at_tz}) }}
                    </span>
                </div>
                <button-base
                    v-if="lead.can_archive"
                    size="sm"
                    style-type="secondary"
                    :disabled="restoring"
                    @click="restoreLead"
                >
                    {{ _mft('crm:archive.restore') }}
                </button-base>
            </div>

            <ui-tabbed
                @changestatustab="setCurrentTab"
                :scroll-overflow="true"
                :display-in-tab-bar="! asWidget"
                :track-state="false"
            >
                <ui-tabbed-tab
                    :title="_mft('crm:leadPage.tabTitle.details')"
                    value="details"
                    :active="true"
                >
                    <div class="space-y-4 xl:space-y-0 xl:grid xl:grid-cols-3 xl:gap-4 items-start">
                        <card-default class="col-span-2">
                            <template #title>
                                <h3 class="mb-2">{{ _mft('crm:contact') }}</h3>
                            </template>
                            <lead-details
                                :lead-id="props.leadId"
                                :show-actions-button="false"
                                @update:lead="updateLead"
                                @reload:lead="loadLead"
                            />
                            <p class="text-xs text-gray-400 flex justify-center space-x-4 mb-0">
                                <span v-if="lead.created_at_tz" class="block"><mf-translate domainkey="crm:addedAt"></mf-translate> {{ lead.created_at_tz }}</span>
                                <span v-if="lead.updated_at_tz && lead.created_at_tz !== lead.updated_at_tz" class="block"><mf-translate domainkey="crm:updatedAt"></mf-translate> {{ lead.updated_at_tz }}</span>
                            </p>
                        </card-default>

                        <div>
                            <tags-manage-inline
                                ref="tagsManage"
                                button-size="sm"
                                container="card-default"
                                :lead-id="lead.id"
                            ></tags-manage-inline>

                            <lead-company-details
                                :lead-id="props.leadId"
                                @update:lead-company="loadLead"
                            />
                        </div>
                    </div>
                </ui-tabbed-tab>

                <ui-tabbed-tab
                    :title="_mft('crm:leadPage.tabTitle.contracts')"
                    value="signatures"
                >
                    <div
                        v-if="currentTab === 'signatures'"
                    >
                        <signatures-list-crm
                            :lead-id="lead.id"
                        ></signatures-list-crm>
                    </div>
                </ui-tabbed-tab>

                <ui-tabbed-tab
                    :title="_mft('crm:leadPage.tabTitle.deliveries')"
                    value="deliveries"
                >
                    <div>
                        <div v-if="currentTab === 'deliveries' || showAll" class="mb-4 flex justify-end">
                            <a :href="route('crm.leads.timeline', lead.id)" class="btn btn-secondary">
                                <button-base
                                    style-type="secondary"
                                >
                                    <template #icon>
                                        <i class="fa-solid fa-clock-rotate-left mr-2"></i>
                                    </template>
                                    Manage Automation Timeline
                                </button-base>
                            </a>
                        </div>

                        <deliveries-list-crm
                            v-if="currentTab === 'deliveries'"
                            :lead-id="lead.id"
                            :display-title="false"
                        ></deliveries-list-crm>
                    </div>
                </ui-tabbed-tab>

                <ui-tabbed-tab
                    :title="_mft('crm:leadPage.tabTitle.notes')"
                    value="notes"
                >
                    <lead-note-card
                        v-if="currentTab === 'notes'"
                        :lead-id="lead.id"
                    ></lead-note-card>
                </ui-tabbed-tab>

                <ui-tabbed-tab
                    :title="_mft('crm:leadPage.tabTitle.actions')"
                    value="actions"
                >
                    <div>
                        <lead-action-card
                            v-if="currentTab === 'actions'"
                            :lead-id="lead.id"
                        ></lead-action-card>
                    </div>
                </ui-tabbed-tab>

                <ui-tabbed-tab
                    :title="_mft('crm:leadPage.tabTitle.deals')"
                    value="deals"
                >
                    <div>
                        <lead-deal-card
                            v-if="currentTab === 'deals'"
                            :lead-id="lead.id"
                            :internal="true"
                        ></lead-deal-card>
                    </div>
                </ui-tabbed-tab>

                <ui-tabbed-tab
                    :title="_mft('crm:leadPage.tabTitle.licenses')"
                    value="licenses"
                >
                    <div>
                        <lead-license-card
                            v-if="currentTab === 'licenses'"
                            :lead-id="lead.id"
                        ></lead-license-card>
                    </div>
                </ui-tabbed-tab>

                <ui-tabbed-tab
                    :title="'Manage'"
                    value="manage"
                >
                    <div
                        v-if="currentTab === 'manage'"
                    >
                        <div class="grid grid-cols-2 gap-8 mt-4">
                            <card-basic class="mb-4">
                                <div>
                                    <crm-lead-move
                                        :lead-id="lead.id"
                                    ></crm-lead-move>
                                </div>
                            </card-basic>

                            <div>
                            </div>
                        </div>

                        <crm-lead-events
                            :lead-id="lead.id"
                            :collapsed="false"
                        ></crm-lead-events>
                    </div>
                </ui-tabbed-tab>
            </ui-tabbed>
        </div>
    </div>
</template>
